/**
 * The form definition for a resident's own settings, from the configuration service.
 *
 * Cached for the session: it changes on deploy, not per resident — the response is the same for
 * everybody, bar the device type backend-api stamps into `meta`, which is why it is keyed on the
 * profile rather than shared outright.
 *
 * A failure is not fatal. The page falls back to what it has always rendered rather than showing a
 * carer nothing, so a configuration service that is down costs the new fields, not the page.
 */

import { useQuery } from '@tanstack/react-query';
import { Profile } from '@ixicare/resources';
import { Schema } from '~modules/devices/components/DeviceConfigForm/schema';

export const useProfileConfigSchema = (profileId: number) => {
  const { data, isLoading } = useQuery({
    queryKey: ['profileConfigSchema', profileId],
    queryFn: () => Profile.getProfileConfigSchema(profileId),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // The resources type and the renderer's own describe the same payload; the renderer's is the one
  // that is load-bearing, as it is for the device form (`Devices.ts` casts the same way).
  return { schema: data as unknown as Schema | undefined, isLoading };
};
